'use client'

import Image from 'next/image'
import React from 'react'
import { useMemo, useState } from 'react'

type ZoomableImageProps = {
  src: string
  alt: string
  fallbackSrc?: string
  /** Kept for API compatibility; zoom is disabled. */
  zoomImages?: { src: string; alt: string }[]
  fill?: boolean
  sizes?: string
  className?: string
  priority?: boolean
  unoptimized?: boolean
  /** Kept for API compatibility; no-op. */
  stopPropagation?: boolean
  children?: React.ReactNode
}

/**
 * Renders an image. Zoom/lightbox has been removed; this component is now a simple image wrapper.
 */
export function ZoomableImage({
  src,
  alt,
  fallbackSrc,
  fill = false,
  sizes,
  className = '',
  priority,
  unoptimized,
  children,
}: ZoomableImageProps) {
  const [currentSrc, setCurrentSrc] = useState(src)
  const safeFallback = useMemo(() => (fallbackSrc && fallbackSrc !== src ? fallbackSrc : null), [fallbackSrc, src])

  React.useEffect(() => {
    setCurrentSrc(src)
  }, [src])

  if (children) {
    return <>{children}</>
  }
  return (
    <Image
      src={currentSrc}
      alt={alt}
      fill={fill}
      sizes={sizes}
      className={className}
      priority={priority}
      unoptimized={unoptimized}
      onError={() => {
        if (safeFallback) setCurrentSrc(safeFallback)
      }}
    />
  )
}
